// `QuotaGate` — generic plan-derived quota gate (US-199).
//
// Wraps an action button (or "+ Connect account" CTA). When `current >=
// limit`, the children are disabled and a tooltip-like banner surfaces
// the upgrade CTA. Otherwise, renders the children normally.

import { type ReactNode } from 'react';

import { useT } from '@barel/i18n';
import { Pill } from '@barel/ui';
import { Link } from '@tanstack/react-router';

import type { BillingPlan } from '@barel/api-client/react';

const NEXT_PLAN: Record<BillingPlan, BillingPlan | null> = {
  discover: 'pro',
  pro: 'scale',
  scale: 'agency',
  agency: null,
};

export interface QuotaGateProps {
  resource: 'accounts' | 'campaigns' | 'aiCalls';
  current: number;
  limit: number;
  plan: BillingPlan;
  children: ReactNode;
}

export function QuotaGate({ resource, current, limit, plan, children }: QuotaGateProps) {
  const t = useT('billing');
  const reached = current >= limit && Number.isFinite(limit);
  const next = NEXT_PLAN[plan];

  return (
    <div
      data-component="QuotaGate"
      data-resource={resource}
      data-reached={reached || undefined}
      className="relative"
    >
      {children}
      {reached ? (
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-default bg-bg-elevated px-3 py-2 text-xs text-secondary">
          <Pill tone="warning" variant="soft" size="sm">
            {t('quota.limitReached')}
          </Pill>
          <span className="flex-1 truncate">
            {t(`quota.${resource}`, { current, limit: Number.isFinite(limit) ? limit : '∞' })}
          </span>
          {next ? (
            <Link
              to="/settings/$section"
              params={{ section: 'billing' }}
              className="text-xs font-medium text-accent hover:underline"
              data-testid="quota-upgrade-cta"
            >
              {t('quota.upgradeCta', {
                plan: next.charAt(0).toUpperCase() + next.slice(1),
              })}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function QuotaPill({
  resource,
  current,
  limit,
}: {
  resource: 'accounts' | 'campaigns' | 'aiCalls';
  current: number;
  limit: number;
}) {
  const t = useT('billing');
  const reached = current >= limit && Number.isFinite(limit);
  return (
    <Pill tone={reached ? 'warning' : 'neutral'} variant="soft" size="sm" data-testid="quota-pill">
      {t(`quota.${resource}`, { current, limit: Number.isFinite(limit) ? limit : '∞' })}
    </Pill>
  );
}
